/**
 * @author michalstolarski
 * @since 2023-9-12
 */

import * as React from "react";
import styled, { useTheme } from "styled-components";

import { PaymentAlias, PaymentAliasType } from "@typings/payments";
import { PAYMENT_METHOD_STATUS } from "@typings/globals";

import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { PaymentAliasLogo } from "@ui/Atoms/PaymentAliasLogo";
import { InfoLabel } from "@ui/Atoms/InfoLabel";

import { IntlDateFormat, intlFormatDate } from "@services/IntlFormatDate";

interface Props extends Omit<PaymentAlias, "default" | "uuid"> {
  testId?: string;
  className?: string;
  translations: {
    expiresText: string;
    cardExpiredText: string;
  };
}

const PaymentMethodDetails = (props: Props): React.ReactElement => {
  const theme = useTheme();
  const expiredDate = React.useMemo((): null | string => {
    if (props.type === PaymentAliasType.TWI) return null;

    return intlFormatDate(
      new Date(props.expirationDate),
      IntlDateFormat.altShortDateMonth,
    );
  }, [props.type, props.expirationDate]);

  return (
    <TestWrapper testId={props.testId}>
      <Container className={props.className}>
        <Logo>
          <PaymentAliasLogo
            version="v2"
            type={props.type}
            style={{ height: theme.margins.base_x3 }}
          />
        </Logo>

        <div>
          <CardNumber>{props.maskedNumber}</CardNumber>

          {!!expiredDate && (
            <ExpirationDate>
              {props.translations.expiresText}: {expiredDate}
            </ExpirationDate>
          )}

          {props.paymentMethodStatus === PAYMENT_METHOD_STATUS.EXPIRED && (
            <ExpiredLabel variant="danger">
              {props.translations.cardExpiredText}
            </ExpiredLabel>
          )}
        </div>
      </Container>
    </TestWrapper>
  );
};

export { Props as PaymentMethodDetailsProps, PaymentMethodDetails };

const Container = styled.div`
  display: flex;
  gap: ${props => props.theme.margins.base_x2};
`;

const Logo = styled.div`
  overflow: hidden;
  align-self: flex-start;
`;

const CardNumber = styled.div`
  font-size: ${props => props.theme.fonts.sizes.body};
  line-height: ${props => props.theme.fonts.lineHeights.normal};
`;

const ExpirationDate = styled.div`
  font-size: ${props => props.theme.fonts.sizes.body2};
  line-height: ${props => props.theme.fonts.lineHeights.normal};
`;

const ExpiredLabel = styled(InfoLabel)`
  line-height: ${props => props.theme.fonts.lineHeights.caption};
`;
